import { useState } from "react";
import { ShieldAlert, Lock, ArrowRight, X, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import "./security-modal.css";

const SecurityModal = ({ isOpen, onClose, onComplete }) => {
  const { reauthenticate, updateSessionPassword } = useAuth();
  const { showAlert } = useToast();
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match!");
      return;
    }

    if (newPassword.length < 8) {
      setError("Security protocol requires at least 8 characters.");
      return;
    }

    setLoading(true);
    try {
      // 1. Re-authenticate with current credentials
      await reauthenticate(currentPassword);
      
      // 2. Update to new password
      await updateSessionPassword(newPassword);
      
      // 3. Complete and close
      if (onComplete) onComplete();
      showAlert("Managed Identity Secured: Password Updated Successfully!", "success");
      onClose();
    } catch (err) {
      setError(err.message || "Authorized Access Denied: Security Breach Detected.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="security-modal-backdrop" onClick={onClose}>
      <div className="security-modal-container glass-card" onClick={(e) => e.stopPropagation()}>
        <button className="abort-btn position-absolute top-2 right-2" onClick={onClose}>
          <X size={20} />
        </button>

        <header className="security-modal-header">
          <div className="security-shield-accent">
            <ShieldAlert size={32} />
          </div>
          <h2>Security Verification</h2>
          <p>Sensitive operation detected. Please re-validate your administrative credentials to continue.</p>
        </header>

        {error && (
          <div className="security-error-toast">
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="security-form">
          <div className="security-input-group">
            <label><Lock size={14} /> Current Security Key</label>
            <div className="security-field-wrapper">
              <input 
                type="password" 
                placeholder="••••••••" 
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="security-input-group">
            <label><Lock size={14} /> New Security Key</label>
            <div className="security-field-wrapper">
              <input 
                type="password" 
                placeholder="••••••••" 
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="security-input-group">
            <label><Lock size={14} /> Confirm New Key</label>
            <div className="security-field-wrapper">
              <input 
                type="password" 
                placeholder="••••••••" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>
          </div>

          <footer className="security-actions">
            <button 
              className="authorize-btn" 
              type="submit" 
              disabled={loading}
            >
              {loading ? <div className="security-loader"></div> : "Authorize Update"}
              {!loading && <ArrowRight size={18} />}
            </button>
            <button 
              className="abort-btn" 
              type="button" 
              onClick={onClose}
              disabled={loading}
            >
              Abort Session
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
};

export default SecurityModal;
